import { formatCurrency } from '@/lib';
import type { Quote } from '@/model/quotesModel';
import { FEATURE_BENEFITS_LABELS } from '@/features/shop-insurance/lib/eligibility-tooltip-content';
import { getLtcTiming } from '@/features/shop-insurance/lib/ltc-timing';
import { MAX_DURATION_YEARS } from '@/features/shop-insurance/lib/filter-matchers';
import { useBenefitDesignInputs } from '@/features/shop-insurance/stores/benefit-design-inputs-store';
import {
  useBenefitTypeFilterActions,
  useSelectedBenefitPaymentType,
} from '@/features/shop-insurance/stores/benefit-type-filter-store';
import {
  useCarrierExclusionsActions,
  useHiddenCarriers,
} from '@/features/shop-insurance/stores/carrier-exclusions-store';
import {
  useEligibilityFiltersActions,
  useIncludeCouplesDiscount,
  useSelectedEligibilityFilters,
  useSelectedFeatureBenefitsFilters,
} from '@/features/shop-insurance/stores/eligibility-filters-store';
import type {
  BenefitDesignInputs,
  CombinedFilterType,
  FilterName,
  InflationTypeFilterValue,
  PaymentOption,
  ShopInsuranceMode,
} from '@/features/shop-insurance/types';
import type { BenefitPaymentTypeFilter } from '@/features/shop-insurance/stores/benefit-type-filter-store';
import type { BenefitDesignAppliedFilterRemoval } from '@/features/shop-insurance/hooks/use-benefit-design-draft-filters';
import { UNDERWRITING_STATUS_CONFIG, UNDERWRITING_STATUS_FILTERS } from '@shared';

export interface ActiveShopFilterChip {
  key: string;
  label: string;
  onRemove?: () => void;
}

interface ActiveFilterActions {
  removePremiumFilter: (filter: FilterName) => void;
  resetBenefitDesignDuration: () => void;
  resetBenefitDesignInflation: () => void;
  resetBenefitDesignInflationType: () => void;
  removeEligibilityFilter: (filter: string) => void;
  resetEligibilityFilters: () => void;
  removeFeatureBenefitsFilter: (filter: string) => void;
  unhideCarrier: (carrier: string) => void;
  includeCouplesDiscount: () => void;
  resetPremiumBenefitType: () => void;
  resetBenefitDesignBenefitType: () => void;
}

export interface BuildActiveShopFilterChipsParams {
  mode: ShopInsuranceMode;
  paymentOption: PaymentOption;
  premiumValue: number;
  appliedPremiumFilters: FilterName[];
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: number;
  inflationProtectionRangeValue: CombinedFilterType;
  useMinimumInflation: boolean;
  inflationTypeFilterValue: InflationTypeFilterValue;
  benefitDesignInputs: BenefitDesignInputs;
  premiumBenefitPaymentType: BenefitPaymentTypeFilter;
  policyIssueAge?: number;
  careStartAge?: number;
  isGapFillMode: boolean;
  showLifeFilters: boolean;
  showEligibilityFilter: boolean;
  hasCouplesDiscountQuotes: boolean;
  includeCouplesDiscount: boolean;
  selectedEligibilityFilters: string[];
  selectedFeatureBenefitsFilters: string[];
  hiddenCarriers: string[];
  actions: ActiveFilterActions;
}

interface UseShopInsuranceActiveFiltersParams {
  mode: ShopInsuranceMode;
  paymentOption: PaymentOption;
  premiumValue: number;
  appliedPremiumFilters: FilterName[];
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: number;
  inflationProtectionRangeValue: CombinedFilterType;
  useMinimumInflation: boolean;
  inflationTypeFilterValue: InflationTypeFilterValue;
  client: Client | undefined;
  quotes: Quote[] | undefined;
  isGapFillMode: boolean;
  showLifeFilters: boolean;
  showEligibilityFilter: boolean;
  onRemovePremiumFilter: (filter: FilterName) => void;
  onRemoveBenefitDesignFilter: (removal: BenefitDesignAppliedFilterRemoval) => void;
}

function formatInflationRange([min, max]: number[]): string {
  if (min === 0 && max === 5) return 'Any';
  if (min === max) return `${min}%`;
  return `${min}%-${max}%`;
}

function formatDurationRange([min, max]: number[]): string {
  if (min === 0 && max === Infinity) return 'Any';

  const minLabel = min >= MAX_DURATION_YEARS || min === Infinity ? 'Lifetime' : String(min);
  const maxLabel = max >= MAX_DURATION_YEARS || max === Infinity ? 'Lifetime' : String(max);

  if (minLabel === maxLabel) {
    return minLabel === 'Lifetime' ? minLabel : `${minLabel} ${min === 1 ? 'Year' : 'Years'}`;
  }

  if (maxLabel === 'Lifetime') return `${minLabel} Years-Lifetime`;
  return `${minLabel}-${maxLabel} Years`;
}

function formatInflationFilter(value: CombinedFilterType): string {
  if (value.type === 'combined') return `${formatInflationRange(value.combined)} Inflation`;
  return `AOB: ${formatInflationRange(value.aob)}, COB: ${formatInflationRange(value.cob)} Inflation`;
}

function formatDurationFilter(value: CombinedFilterType): string {
  if (value.type === 'combined') return `${formatDurationRange(value.combined)} Duration`;
  return `AOB: ${formatDurationRange(value.aob)}, COB: ${formatDurationRange(value.cob)} Duration`;
}

function formatBenefitType(value: 'reimbursement' | 'indemnity'): string {
  return `${value === 'reimbursement' ? 'Reimbursement' : 'Indemnity'} Benefit`;
}

function isEligibilityFilterApplied(selectedFilters: string[]): boolean {
  const defaultFilters = UNDERWRITING_STATUS_FILTERS.map(filter => filter.value);
  return (
    selectedFilters.length !== defaultFilters.length ||
    defaultFilters.some(filter => !selectedFilters.includes(filter))
  );
}

export function buildActiveShopFilterChips({
  mode,
  paymentOption,
  premiumValue,
  appliedPremiumFilters,
  benefitDurationValue,
  inflationProtectionValue,
  inflationProtectionRangeValue,
  useMinimumInflation,
  inflationTypeFilterValue,
  benefitDesignInputs,
  premiumBenefitPaymentType,
  policyIssueAge,
  careStartAge,
  isGapFillMode,
  showLifeFilters,
  showEligibilityFilter,
  hasCouplesDiscountQuotes,
  includeCouplesDiscount,
  selectedEligibilityFilters,
  selectedFeatureBenefitsFilters,
  hiddenCarriers,
  actions,
}: BuildActiveShopFilterChipsParams): ActiveShopFilterChip[] {
  const chips: ActiveShopFilterChip[] = [];

  if (mode === 'premium-optimization') {
    for (const filter of appliedPremiumFilters) {
      if (filter === 'premium' && !isGapFillMode) {
        chips.push({ key: 'premium', label: `${formatCurrency(premiumValue)} Premium` });
      }
      if (filter === 'inflationProtection') {
        chips.push({
          key: 'inflationProtection',
          label: useMinimumInflation
            ? `${inflationProtectionValue}%+ Inflation`
            : formatInflationFilter(inflationProtectionRangeValue),
          onRemove: () => actions.removePremiumFilter(filter),
        });
      }
      if (filter === 'inflationType') {
        chips.push({
          key: 'inflationType',
          label: `${inflationTypeFilterValue === 'compound' ? 'Compound' : 'Simple'} Inflation`,
          onRemove: () => actions.removePremiumFilter(filter),
        });
      }
      if (filter === 'benefitDuration') {
        chips.push({
          key: 'benefitDuration',
          label: formatDurationFilter(benefitDurationValue),
          onRemove: () => actions.removePremiumFilter(filter),
        });
      }
    }

    const hasPremiumChip = appliedPremiumFilters.includes('premium') && !isGapFillMode;

    chips.splice(hasPremiumChip ? 1 : 0, 0, {
      key: 'paymentOption',
      label: `Payment: ${paymentOption}`,
    });

    if (showLifeFilters && premiumBenefitPaymentType !== 'all') {
      chips.splice(hasPremiumChip ? 2 : 1, 0, {
        key: 'benefitPaymentType',
        label: formatBenefitType(premiumBenefitPaymentType),
        onRemove: actions.resetPremiumBenefitType,
      });
    }

    if (showLifeFilters && hasCouplesDiscountQuotes && !includeCouplesDiscount) {
      chips.push({
        key: 'couplesDiscount',
        label: 'Couples Discount excluded',
        onRemove: actions.includeCouplesDiscount,
      });
    }

    selectedFeatureBenefitsFilters.forEach(filter => {
      chips.push({
        key: `feature:${filter}`,
        label: FEATURE_BENEFITS_LABELS[filter] || filter,
        onRemove: () => actions.removeFeatureBenefitsFilter(filter),
      });
    });
  } else {
    const activeMonthlyBenefit = benefitDesignInputs.monthlyBenefit;
    if (activeMonthlyBenefit !== null) {
      const isCareStart = benefitDesignInputs.monthlyBenefitTiming === 'care-start';
      const age = isCareStart ? careStartAge : policyIssueAge;
      chips.push({
        key: 'monthlyBenefit',
        label: `${formatCurrency(activeMonthlyBenefit)}/mo at ${isCareStart ? 'LTC start' : 'issue'}${age === undefined ? '' : ` (age ${age})`}`,
      });
    }

    if (benefitDesignInputs.benefitPaymentType !== null) {
      chips.push({
        key: 'benefitPaymentType',
        label: formatBenefitType(benefitDesignInputs.benefitPaymentType),
        onRemove: actions.resetBenefitDesignBenefitType,
      });
    }

    if (benefitDesignInputs.benefitDuration !== null) {
      chips.push({
        key: 'benefitDuration',
        label: formatDurationFilter(benefitDesignInputs.benefitDuration),
        onRemove: actions.resetBenefitDesignDuration,
      });
    }

    if (useMinimumInflation && benefitDesignInputs.inflationProtection !== null) {
      chips.push({
        key: 'inflationProtection',
        label: `${benefitDesignInputs.inflationProtection}%+ Inflation`,
        onRemove: actions.resetBenefitDesignInflation,
      });
    } else if (!useMinimumInflation && benefitDesignInputs.inflationProtectionRange !== null) {
      chips.push({
        key: 'inflationProtection',
        label: formatInflationFilter(benefitDesignInputs.inflationProtectionRange),
        onRemove: actions.resetBenefitDesignInflation,
      });
    }

    if (benefitDesignInputs.inflationTypes !== null) {
      const inflationTypes = benefitDesignInputs.inflationTypes
        .map(type => (type === 'compound' ? 'Compound' : 'Simple'))
        .join(' or ');
      chips.push({
        key: 'inflationType',
        label: `${inflationTypes} Inflation`,
        onRemove: actions.resetBenefitDesignInflationType,
      });
    }
  }

  // Eligibility belongs to By Premium. By Benefit deliberately has no eligibility controls
  // and does not filter its grouped quote results by underwriting status.
  if (
    mode === 'premium-optimization' &&
    showEligibilityFilter &&
    isEligibilityFilterApplied(selectedEligibilityFilters)
  ) {
    if (selectedEligibilityFilters.length === 0) {
      chips.push({
        key: 'eligibility:none',
        label: 'No eligibility statuses',
        onRemove: actions.resetEligibilityFilters,
      });
    } else {
      selectedEligibilityFilters.forEach(filter => {
        const config =
          UNDERWRITING_STATUS_CONFIG[filter as keyof typeof UNDERWRITING_STATUS_CONFIG];
        const label = config?.label || filter;
        chips.push({
          key: `eligibility:${filter}`,
          label,
          onRemove: () => actions.removeEligibilityFilter(filter),
        });
      });
    }
  }

  hiddenCarriers.forEach(carrier => {
    chips.push({
      key: `carrier:${carrier}`,
      label: `${carrier} hidden`,
      onRemove: () => actions.unhideCarrier(carrier),
    });
  });

  return chips;
}

export function useShopInsuranceActiveFilters({
  mode,
  paymentOption,
  premiumValue,
  appliedPremiumFilters,
  benefitDurationValue,
  inflationProtectionValue,
  inflationProtectionRangeValue,
  useMinimumInflation,
  inflationTypeFilterValue,
  client,
  quotes,
  isGapFillMode,
  showLifeFilters,
  showEligibilityFilter,
  onRemovePremiumFilter,
  onRemoveBenefitDesignFilter,
}: UseShopInsuranceActiveFiltersParams): ActiveShopFilterChip[] {
  const benefitDesignInputs = useBenefitDesignInputs();
  const premiumBenefitPaymentType = useSelectedBenefitPaymentType();
  const hiddenCarriers = useHiddenCarriers();
  const selectedEligibilityFilters = useSelectedEligibilityFilters();
  const selectedFeatureBenefitsFilters = useSelectedFeatureBenefitsFilters();
  const includeCouplesDiscount = useIncludeCouplesDiscount();

  const benefitTypeActions = useBenefitTypeFilterActions();
  const carrierActions = useCarrierExclusionsActions();
  const eligibilityActions = useEligibilityFiltersActions();

  const { policyIssueAge, careStartAge } = getLtcTiming(
    client?.intakeSurvey?.clientAge,
    client?.appliedInferenceSet?.ltcAtAge,
  );

  return buildActiveShopFilterChips({
    mode,
    paymentOption,
    premiumValue,
    appliedPremiumFilters,
    benefitDurationValue,
    inflationProtectionValue,
    inflationProtectionRangeValue,
    useMinimumInflation,
    inflationTypeFilterValue,
    benefitDesignInputs,
    premiumBenefitPaymentType,
    policyIssueAge,
    careStartAge,
    isGapFillMode,
    showLifeFilters,
    showEligibilityFilter,
    hasCouplesDiscountQuotes:
      quotes?.some(quote => quote.policyType === 'Couples Discount') ?? false,
    includeCouplesDiscount,
    selectedEligibilityFilters,
    selectedFeatureBenefitsFilters,
    hiddenCarriers,
    actions: {
      removePremiumFilter: onRemovePremiumFilter,
      resetBenefitDesignDuration: () => onRemoveBenefitDesignFilter({ type: 'benefitDuration' }),
      resetBenefitDesignInflation: () =>
        onRemoveBenefitDesignFilter({ type: 'inflationProtection' }),
      resetBenefitDesignInflationType: () => onRemoveBenefitDesignFilter({ type: 'inflationType' }),
      removeEligibilityFilter: eligibilityActions.removeEligibilityFilter,
      resetEligibilityFilters: () =>
        eligibilityActions.setSelectedEligibilityFilters(
          UNDERWRITING_STATUS_FILTERS.map(filter => filter.value),
        ),
      removeFeatureBenefitsFilter: eligibilityActions.removeFeatureBenefitsFilter,
      unhideCarrier: carrier => {
        if (mode === 'benefit-design') {
          onRemoveBenefitDesignFilter({ type: 'carrier', carrier });
          return;
        }
        carrierActions.toggleCarrier(carrier);
      },
      includeCouplesDiscount: () => eligibilityActions.setIncludeCouplesDiscount(true),
      resetPremiumBenefitType: () => benefitTypeActions.setSelectedBenefitPaymentType('all'),
      resetBenefitDesignBenefitType: () =>
        onRemoveBenefitDesignFilter({ type: 'benefitPaymentType' }),
    },
  });
}
